/**
 * Shared utilities for template preview rendering.
 * Used by TemplatePreviewModal and TemplateMiniPreview.
 */

import type { SiteTemplate } from '../schemas/validation';
import type { BrandFonts } from '../contexts/CustomColorsContext';
import { isGlobalPalette, deriveComponentColors } from './deriveComponentColors';

/**
 * Extracted theme/brand info for wrapping template previews with the correct providers.
 */
export interface TemplateThemeInfo {
  /** Theme name to pass to ThemeProvider (e.g. 'standard', 'modern', 'bold') */
  themeName: string;
  /** Custom colors to pass to CustomColorsProvider (derived from brand package or empty) */
  customColors: Record<string, any>;
  /** Font settings to pass to CustomColorsProvider */
  fonts?: BrandFonts;
}

/**
 * Extract theme name, custom colors, and fonts from a SiteTemplate's siteData.
 * Handles brand packages (palette → derive component colors) and plain theme names.
 */
export const getTemplateThemeInfo = (template: SiteTemplate): TemplateThemeInfo => {
  const siteData = template.siteData as any;
  const themeName: string = siteData?.theme || 'standard';
  const brandPackage = siteData?.brandPackage;

  let customColors: Record<string, any> = {};
  let fonts: BrandFonts | undefined;

  if (brandPackage) {
    // Extract colors from brand package
    const colors = brandPackage.colors;
    if (colors) {
      if (isGlobalPalette(colors)) {
        // Brand package stores a 6-color palette — derive full component colors
        customColors = deriveComponentColors(colors);
      } else {
        // Already full component colors
        customColors = colors;
      }
    }

    // Extract fonts from brand package
    const bpFonts = brandPackage.fonts;
    if (bpFonts) {
      fonts = {
        headingFont: bpFonts.headingFont,
        bodyFont: bpFonts.bodyFont || bpFonts.fontFamily,
        baseSize: bpFonts.fontSize || bpFonts.baseSize,
      };
    }
  }

  return { themeName, customColors, fonts };
};

/**
 * Normalize components data to always be an array.
 * Handles: null, undefined, empty string, '{}', '[]', objects, arrays
 */
export const normalizeComponents = (components: any): any[] => {
  // Handle null/undefined
  if (components == null) {
    return [];
  }

  // Handle string (JSON)
  if (typeof components === 'string') {
    const trimmed = components.trim();
    if (!trimmed || trimmed === '{}' || trimmed === '[]') {
      return [];
    }
    try {
      const parsed = JSON.parse(trimmed);
      // Recursively normalize the parsed result
      return normalizeComponents(parsed);
    } catch {
      return [];
    }
  }

  // Handle array
  if (Array.isArray(components)) {
    return components;
  }

  // Handle object with components property (e.g., { components: [...] })
  if (typeof components === 'object' && components.components) {
    return normalizeComponents(components.components);
  }

  // Handle plain object (not an array) - return empty
  if (typeof components === 'object') {
    return [];
  }

  return [];
};

/**
 * Split layout components into the header (before content-area) and the footer
 * (after it). A layout with no content-area still shows a navbar on top and a
 * footer at the bottom.
 */
export const processLayoutComponents = (
  layoutComponents: any[] | null | undefined
): { headerComponents: any[]; footerComponents: any[] } => {
  if (!layoutComponents || !Array.isArray(layoutComponents)) {
    return { headerComponents: [], footerComponents: [] };
  }

  const sorted = [...layoutComponents].sort(
    (a, b) => (a.order || a.orderPosition || 0) - (b.order || b.orderPosition || 0)
  );

  const contentAreaIndex = sorted.findIndex(c => c.type === 'content-area');
  if (contentAreaIndex === -1) {
    return {
      headerComponents: sorted.filter(c => c.type === 'navbar' || c.type === 'simple-header'),
      footerComponents: sorted.filter(c => c.type === 'footer'),
    };
  }

  return {
    headerComponents: sorted.slice(0, contentAreaIndex),
    footerComponents: sorted.slice(contentAreaIndex + 1),
  };
};
